import React from 'react';
import { useStyles, cx } from '../../../lib/styles';
import {
  Brand,
  Color,
  FontFamily,
  FontWeight,
  FontSize,
} from '../../../lib/theme';

import typographyStyles from './Typography_styles';

export interface TypographyProps {
  /** The brand to use for calculating colours. */
  brand?: Brand;

  /** The colour to use for the default text color. Any links will use `linkColor` instead. */
  color?: Color;

  /** Any additional classnames to be placed on the container element. */
  className?: string;

  /**
   * Whether headings should fluidly resize to a larger hierachy on bigger devices (tablet, desktop).
   *
   * @default true
   */
  fluid?: boolean;

  /**
   * The colour to use for anchor tags.
   *
   * @default 'secondary'
   */
  linkColor?: Color;

  /**
   * The text decoration to use for anchor tags.
   *
   * @default 'underline'
   */
  linkDecoration?: string;

  /**
   * The font family to use.
   *
   * @default 'primary'
   */
  family?: FontFamily;

  /**
   * The font weight to use. By default headings will be bold and body will
   * be normal. If you set this, both will be forced to the weight you choose.
   */
  weight?: FontWeight;

  /**
   * The font size for body text. This will not affect headings.
   *
   * @default 'm'
   */
  size?: FontSize;

  /**
   * The text aligment to use.
   *
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';

  /** Set the HTML to be dangerously outputted. Works the same as the React built-in. */
  dangerouslySetInnerHTML?: { __html: string };

  /** The children to be styled with the respective font styling. */
  children?: React.ReactNode;
}

/**
 * A block of text. Also supports links and headings.
 * All of the text styling matches our design guide and heading hierarchy.
 */
export const Typography = (props: TypographyProps) => {
  const { children, className, dangerouslySetInnerHTML } = props;
  const styles = useStyles(typographyStyles(props));

  return dangerouslySetInnerHTML ? (
    <div
      className={cx('Typography', styles.container, className)}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    />
  ) : (
    <div className={cx(styles.container, className)}>{children}</div>
  );
};

export default Typography;
